"use strict";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { CircularProgress, Tooltip } from "@mui/material";
import RecordVoiceOverOutlinedIcon from "@mui/icons-material/RecordVoiceOverOutlined";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { getCharacterLabel } from "./CharacterSelector.js";
import { playExclusively } from "./exclusiveAudio.js";
import useRedoOffers from "./useRedoOffers.js";
import { VOICE_CREATION_MODES } from "./voiceCreationModes.js";
import { isRedoJobActive } from "../../features/projectWork/voiceRedoJobs.js";
import { getVoiceSample } from "../../features/projectWork/voiceSamples.js";
import classes from "../../Assets/Styles/CharacterVoicePanel.module.css";

const capitalize = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "Voice";

const shortVoiceId = (voiceId) =>
  voiceId && voiceId.length > 14
    ? `${voiceId.slice(0, 6)}…${voiceId.slice(-4)}`
    : voiceId;

// Each emotion keeps a recognisable tint; others get one from a small palette.
const EMOTION_TINTS = {
  neutral: { background: "#e2e8f0", color: "#334155" },
  happy: { background: "#fef3c7", color: "#92400e" },
  sad: { background: "#dbeafe", color: "#1e40af" },
  angry: { background: "#fee2e2", color: "#b91c1c" },
  afraid: { background: "#ede9fe", color: "#6d28d9" },
};
const EXTRA_TINTS = [
  { background: "#dcfce7", color: "#166534" },
  { background: "#ccfbf1", color: "#115e59" },
  { background: "#fce7f3", color: "#9d174d" },
  { background: "#ffedd5", color: "#9a3412" },
];

const emotionTint = (emotion = "") => {
  const key = emotion.toLowerCase();
  if (EMOTION_TINTS[key]) return EMOTION_TINTS[key];
  let hash = 0;
  for (const char of key) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return EXTRA_TINTS[hash % EXTRA_TINTS.length];
};

// Refetch a sample's signed link this long before it expires.
const SAMPLE_URL_MARGIN_MS = 30 * 1000;

/**
 * Plays samples of a character's voices, one at a time (also pausing any
 * other voice preview). Samples are fetched on first play and reused while
 * their link is valid. `playing` is { voiceId, status: "loading" | "playing" }.
 */
const useVoiceSamplePlayer = ({ projectId, languageCode, speakerId }) => {
  const audioRef = useRef(null);
  const requestedRef = useRef(null);
  const [samples, setSamples] = useState({});
  const [playing, setPlaying] = useState({ voiceId: null, status: null });

  const audio = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      const reset = () => {
        // A sample that's still loading isn't affected by an earlier pause.
        setPlaying((current) =>
          current.status === "loading"
            ? current
            : { voiceId: null, status: null }
        );
      };
      audioRef.current.addEventListener("ended", reset);
      audioRef.current.addEventListener("pause", reset);
    }
    return audioRef.current;
  };

  const stop = useCallback(() => {
    requestedRef.current = null;
    audioRef.current?.pause();
    setPlaying({ voiceId: null, status: null });
  }, []);

  useEffect(() => stop, [stop]);

  const toggle = async (voiceId) => {
    if (playing.voiceId === voiceId) {
      stop();
      return;
    }

    requestedRef.current = voiceId;
    audioRef.current?.pause();
    setPlaying({ voiceId, status: "loading" });
    try {
      let sample = samples[voiceId];
      const expiresAt = Date.parse(sample?.expiresAt || "");
      if (
        !sample ||
        (expiresAt && expiresAt - SAMPLE_URL_MARGIN_MS < Date.now())
      ) {
        const data = await getVoiceSample({
          projectId,
          languageCode,
          speakerId,
          voiceId,
        });
        sample = {
          url: data.playback_url,
          expiresAt: data.playbackUrlExpiresAt,
          text: data.text,
        };
        setSamples((previous) => ({ ...previous, [voiceId]: sample }));
      }
      // Another voice was picked while this one loaded.
      if (requestedRef.current !== voiceId) return;

      const player = audio();
      player.src = sample.url;
      await playExclusively(player);
      setPlaying({ voiceId, status: "playing" });
    } catch (error) {
      if (requestedRef.current !== voiceId) return;
      console.error("Unable to play the voice sample:", error);
      toast.error(error?.response?.data?.error || "Couldn't play this voice.");
      setPlaying({ voiceId: null, status: null });
    }
  };

  return { samples, playing, toggle, stop };
};

const copyVoiceId = async (voiceId) => {
  try {
    await navigator.clipboard.writeText(voiceId);
    toast.success("Voice ID copied");
  } catch (error) {
    toast.error("Couldn't copy the voice ID");
  }
};

/**
 * A character's trained voices, each with a way to redo all of the
 * character's lines with it (run on the server, like redoing after a remix).
 */
const CharacterVoicesView = ({
  character,
  avatar,
  projectId,
  languageCode,
  redoJobs,
  onBack,
  onOpenForm,
  onDelete,
  deleteDisabledReason,
  onRename,
  renameField,
}) => {
  const name = getCharacterLabel(character);
  const speaker = character.speaker;
  const voices = (
    Array.isArray(character.voices) ? character.voices : []
  ).filter((voice) => voice?.voice_id);

  const getCharacterName = useCallback(() => name, [name]);
  const redo = useRedoOffers({
    projectId,
    languageCode,
    redoJobs,
    getCharacterName,
  });

  // Several voices can share an emotion; number them so they're told apart.
  const emotionTotals = voices.reduce((totals, voice) => {
    const key = capitalize(voice.emotion);
    return { ...totals, [key]: (totals[key] || 0) + 1 };
  }, {});
  const emotionSeen = {};
  const voiceLabels = voices.map((voice) => {
    const key = capitalize(voice.emotion);
    emotionSeen[key] = (emotionSeen[key] || 0) + 1;
    return emotionTotals[key] > 1 ? `${key} ${emotionSeen[key]}` : key;
  });

  const player = useVoiceSamplePlayer({
    projectId,
    languageCode,
    speakerId: speaker,
  });

  const runActive = isRedoJobActive(redoJobs.visibleJobs[speaker]);
  const cardVoiceId = redo.hasCard(speaker) ? redo.cardVoiceId(speaker) : null;
  const cardIndex = voices.findIndex((voice) => voice.voice_id === cardVoiceId);

  return (
    <>
      <div className={classes["FormHeader"]}>
        <button
          type="button"
          className={classes["IconButton"]}
          onClick={() => {
            player.stop();
            onBack();
          }}
          aria-label="Back to characters"
        >
          <ArrowBackIcon fontSize="small" />
        </button>
        {renameField || (
          <>
            {avatar}
            <span className={classes["VoicesHeaderText"]}>
              <span className={classes["FormTitle"]}>{name}</span>
              <span className={classes["VoicesHeaderMeta"]}>
                {voices.length} {voices.length === 1 ? "voice" : "voices"}
              </span>
            </span>
            {onRename && (
              <Tooltip title="Rename">
                <button
                  type="button"
                  className={`${classes["IconButton"]} ${classes["VoicesHeaderRename"]}`}
                  aria-label={`Rename ${name}`}
                  onClick={onRename}
                >
                  <EditOutlinedIcon fontSize="small" />
                </button>
              </Tooltip>
            )}
          </>
        )}
      </div>

      <div className={classes["Body"]}>
        {voices.length === 0 ? (
          <div className={classes["VoicesEmpty"]}>
            <span className={classes["VoicesEmptyIcon"]} aria-hidden="true">
              <RecordVoiceOverOutlinedIcon />
            </span>
            <p className={classes["VoicesEmptyTitle"]}>No trained voices yet</p>
            <p className={classes["VoicesEmptyText"]}>
              Create a voice for {name} with a remix or a design.
            </p>
            <div className={classes["VoicesEmptyActions"]}>
              {[VOICE_CREATION_MODES.remix, VOICE_CREATION_MODES.design].map(
                (mode) => {
                  const Icon = mode.icon;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      className={classes["EmptyStateButton"]}
                      onClick={() => onOpenForm(mode.value)}
                    >
                      <Icon sx={{ fontSize: 18 }} />
                      {mode.label}
                    </button>
                  );
                }
              )}
            </div>
          </div>
        ) : (
          <>
            <div className={classes["NewVoiceBar"]}>
              <span>New voice</span>
              {[VOICE_CREATION_MODES.remix, VOICE_CREATION_MODES.design].map(
                (mode) => {
                  const Icon = mode.icon;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      className={classes["NewVoiceButton"]}
                      onClick={() => {
                        player.stop();
                        onOpenForm(mode.value);
                      }}
                    >
                      <Icon sx={{ fontSize: 16 }} />
                      {mode.shortLabel}
                    </button>
                  );
                }
              )}
            </div>

            <div className={classes["VoicesHint"]}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
              <span>
                Pick a voice to redo all of {name}'s lines with it. Current
                takes stay in each line's history.
              </span>
            </div>

            {redo.hasCard(speaker) &&
              cardIndex === -1 &&
              redo.renderCard(speaker)}

            <ul className={classes["VoiceList"]}>
              {voices.map((voice, index) => {
                const emotion = voiceLabels[index];
                const isSelected = index === cardIndex;
                return (
                  <li key={`${voice.voice_id}-${index}`}>
                    {isSelected && (
                      <div className={classes["VoiceCard"]}>
                        {redo.renderCard(speaker)}
                      </div>
                    )}
                    <div
                      className={`${classes["VoiceTile"]} ${
                        isSelected ? classes["VoiceTileSelected"] : ""
                      }`}
                    >
                      <button
                        type="button"
                        className={`${classes["VoicePlay"]} ${
                          player.playing.voiceId === voice.voice_id &&
                          player.playing.status === "playing"
                            ? classes["VoicePlaying"]
                            : ""
                        }`}
                        style={emotionTint(voice.emotion)}
                        onClick={() => player.toggle(voice.voice_id)}
                        aria-label={`${
                          player.playing.voiceId === voice.voice_id
                            ? "Stop"
                            : "Play"
                        } the ${emotion.toLowerCase()} voice`}
                        title={
                          player.samples[voice.voice_id]?.text
                            ? `“${player.samples[voice.voice_id].text}”`
                            : "Hear this voice say one of the lines"
                        }
                      >
                        {player.playing.voiceId !== voice.voice_id ? (
                          <PlayArrowRoundedIcon sx={{ fontSize: 20 }} />
                        ) : player.playing.status === "loading" ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <PauseRoundedIcon sx={{ fontSize: 20 }} />
                        )}
                      </button>

                      <span className={classes["VoiceTileText"]}>
                        <span className={classes["VoiceName"]}>{emotion}</span>
                        <span className={classes["VoiceIdRow"]}>
                          <span
                            className={classes["VoiceId"]}
                            title={voice.voice_id}
                          >
                            {shortVoiceId(voice.voice_id)}
                          </span>
                          <button
                            type="button"
                            className={classes["VoiceCopy"]}
                            onClick={() => copyVoiceId(voice.voice_id)}
                            aria-label={`Copy the ${emotion.toLowerCase()} voice's ID`}
                            title="Copy voice ID"
                          >
                            <ContentCopyRoundedIcon sx={{ fontSize: 13 }} />
                          </button>
                        </span>
                        {player.samples[voice.voice_id]?.text && (
                          <span
                            className={classes["VoiceSampleText"]}
                            title={player.samples[voice.voice_id].text}
                          >
                            “{player.samples[voice.voice_id].text}”
                          </span>
                        )}
                      </span>

                      <button
                        type="button"
                        className={classes["VoiceRedoButton"]}
                        disabled={runActive}
                        title={
                          runActive
                            ? `${name}'s lines are already being redone`
                            : "Redo all lines"
                        }
                        aria-label={`Redo all of ${name}'s lines with the ${emotion.toLowerCase()} voice`}
                        onClick={() =>
                          redo.offer(
                            speaker,
                            { voiceId: voice.voice_id, emotion: voice.emotion },
                            { askFirst: false }
                          )
                        }
                      >
                        <AutorenewRoundedIcon sx={{ fontSize: 16 }} />
                        <span className={classes["VoiceRedoLabel"]}>
                          Redo all lines
                        </span>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {onDelete && (
        <div className={classes["VoicesFooter"]}>
          <button
            type="button"
            className={classes["DeleteCharacterButton"]}
            onClick={() => {
              player.stop();
              onDelete();
            }}
            disabled={Boolean(deleteDisabledReason)}
            title={deleteDisabledReason || undefined}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            Delete character
          </button>
        </div>
      )}
    </>
  );
};

export default CharacterVoicesView;
